import { Box, Paper, Typography } from '@mui/material'
import { useQueueRefreshErrors, useQueueSummary } from '@src/tools/dead-letter/api/queues-api'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import PoisonQueueMessages from './Components/PoisonQueueMessages'
import QueueRefreshErrors from './Components/QueueRefreshErrors'
import QueueSidebar from './Components/QueueSidebar'
import QueueSummary from './Components/QueueSummary'

const QueuesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { storageAccountName, queueName } = useParams<{ storageAccountName: string; queueName: string }>()

  const [snapshots, isLoading] = useQueueSummary()
  const [refreshErrors] = useQueueRefreshErrors()

  const poisonQueues = useMemo(
    () => snapshots.filter((s) => s.isPoison && s.messageCount > 0),
    [snapshots],
  )

  const isErrorsView = storageAccountName === 'errors' && !queueName
  const selectedQueue = useMemo(
    () => storageAccountName && queueName ? { storageAccountName, queueName } : null,
    [storageAccountName, queueName],
  )
  const activeView = isErrorsView ? 'errors' as const : selectedQueue ? 'queue' as const : 'summary' as const

  const selectedSnapshot = useMemo(
    () => selectedQueue ? snapshots.find((s) => s.storageAccountName === selectedQueue.storageAccountName && s.queueName === selectedQueue.queueName) : null,
    [snapshots, selectedQueue],
  )

  const handleSelectSummary = useCallback(() => {
    navigate('/dead-letter/queues')
  }, [navigate])

  const handleSelectErrors = useCallback(() => {
    navigate('/dead-letter/queues/errors')
  }, [navigate])

  const handleSelectQueue = useCallback((sa: string, q: string) => {
    navigate(`/dead-letter/queues/${sa}/${q}`)
  }, [navigate])

  const renderContent = () => {
    if (selectedQueue) {
      return (
        <PoisonQueueMessages
          storageAccountName={selectedQueue.storageAccountName}
          queueName={selectedQueue.queueName}
          friendlyName={selectedSnapshot?.friendlyName ?? selectedQueue.storageAccountName}
          totalMessageCount={selectedSnapshot?.messageCount ?? 0}
        />
      )
    }

    if (isErrorsView) {
      return <QueueRefreshErrors errors={refreshErrors} />
    }

    if (snapshots.length === 0 && !isLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography color="text.secondary">{t('queues.noQueues')}</Typography>
        </Box>
      )
    }

    return <QueueSummary snapshots={snapshots} />
  }

  return (
    <Paper sx={{ mt: 2, mb: 2, borderRadius: 2, display: 'flex', height: 'calc(100vh - 180px)' }}>
      <QueueSidebar
        poisonQueues={poisonQueues}
        isLoading={isLoading}
        selectedQueue={selectedQueue}
        activeView={activeView}
        errorCount={refreshErrors.length}
        onSelectSummary={handleSelectSummary}
        onSelectErrors={handleSelectErrors}
        onSelectQueue={handleSelectQueue}
      />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {renderContent()}
      </Box>
    </Paper>
  )
}

export default QueuesPage
